import React from 'react';
import { StyleSheet, View } from 'react-native';
import { Text, useTheme } from 'react-native-paper';
import { useTranslation } from 'react-i18next';

export interface HonestPaywallBannerProps {
  /** Trial length in days, sourced from PaywallConfig — never hardcoded. */
  trialDays: number;
  /** Days remaining in the trial (can be negative once expired). */
  daysRemaining: number;
  /**
   * True when this banner is shown to a driver operating under the
   * company grace period (Q6) rather than their own trial.
   */
  isCompanyGrace?: boolean;
  /** Remaining days of the company grace period (only used when isCompanyGrace). */
  graceDaysRemaining?: number;
  /** True when the viewer is the owner (controls the "trial ended" copy). */
  isOwner?: boolean;
}

/**
 * "Honest paywall" banner: always visible during the trial, showing exactly
 * how many days are left — this invariant holds regardless of any A/B
 * variant (docs/plans/2026-07-02-milestone-13-registration-paywall.md).
 * ZERO hardcoded day counts — everything comes from server-driven props.
 */
export const HonestPaywallBanner: React.FC<HonestPaywallBannerProps> = ({
  trialDays,
  daysRemaining,
  isCompanyGrace = false,
  graceDaysRemaining,
  isOwner = true,
}) => {
  const { t } = useTranslation('paywall');
  const theme = useTheme();

  const expired = daysRemaining <= 0;

  let message: string;
  if (isCompanyGrace) {
    message = t('banner.trialEndedDriverGrace', { graceDaysRemaining: graceDaysRemaining ?? 0 });
  } else if (expired) {
    message = isOwner
      ? t('banner.trialEndedOwner')
      : t('banner.trialEndedDriverGrace', { graceDaysRemaining: graceDaysRemaining ?? 0 });
  } else if (daysRemaining <= 3) {
    message = t('banner.trialEndingSoon', { daysRemaining });
  } else {
    message = t('banner.honestReminder', { daysRemaining, trialDays });
  }

  const backgroundColor = expired || isCompanyGrace ? theme.colors.errorContainer : theme.colors.secondaryContainer;
  const textColor = expired || isCompanyGrace ? theme.colors.onErrorContainer : theme.colors.onSecondaryContainer;

  return (
    <View
      style={[styles.container, { backgroundColor }]}
      testID="honest-paywall-banner"
      accessibilityRole="alert"
    >
      <Text variant="bodyMedium" style={{ color: textColor }}>
        {message}
      </Text>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: '100%',
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 8,
  },
});

export default HonestPaywallBanner;
